import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Drawer, IconButton, Menu, Typography, useMediaQuery } from '@mui/material';
import { getUserSelector } from '@store/auth';
import { AuthService, PopupsService } from '@services';
import { longTextReplacer } from '@utils';
import { Popups, Routes } from '@enums';
import { NavItem } from '@components/nav-item';
import { Tooltip } from '@elements';
import { Icon } from '@elements/icon';
import { pxToRem } from '@themes';
import { StyledContentContainer, StyledIconBox, StyledMenuItem, StyledTypography } from './side-bar.styles';
import logo from '@images/logo.svg';
import settings from '@images/settings.svg';
import { settingsItems, sideBarItems } from './side-bar.helpers';
/**
 * Sidebar component.
 *
 * @author Ihar Kazlouski
 * @function SideBarComponent
 * @category components
 * @return {FC} side bar component.
 */

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var SideBarComponent = function SideBarComponent() {
  var _ref2;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var navigate = useNavigate();
  var location = useLocation();
  var authUser = useSelector(getUserSelector);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      anchorElUser = _useState2[0],
      setAnchorElUser = _useState2[1];

  var lgUp = useMediaQuery(function (theme) {
    return theme.breakpoints.up('lg');
  }, {
    defaultMatches: true
  });
  var drawerWidth = lgUp ? 340 : 300;
  /**
   * Open user menu.
   *
   * @author Ihar Kazlouski
   * @param {React.MouseEvent<HTMLElement>} event event.
   * @return {void}
   */

  var handleOpenUserMenu = function handleOpenUserMenu(event) {
    setAnchorElUser(event.currentTarget);
  };
  /**
   * Close user menu.
   *
   * @author Ihar Kazlouski
   * @return {void}
   */


  var handleCloseUserMenu = function handleCloseUserMenu() {
    setAnchorElUser(null);
  };
  /**
   * Handle choose menu.
   *
   * @author Ihar Kazlouski
   * @param {string} href route.
   * @return {void}
   */


  var handleChooseMenu = function handleChooseMenu(href) {
    return function () {
      setAnchorElUser(null);

      if (href === Routes.Login) {
        void PopupsService.open(Popups.Default, {
          header: t('components.popups.logout'),
          body: t('components.popups.logoutQuestion'),
          acceptButton: t('components.popups.logout'),
          rejectButton: t('components.popups.cancel'),
          isDelete: true
        }).then(function () {
          try {
            void _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
              return _regeneratorRuntime.wrap(function _callee$(_context) {
                while (1) {
                  switch (_context.prev = _context.next) {
                    case 0:
                      _context.next = 2;
                      return AuthService.apiLogoutPost({});

                    case 2:
                      sessionStorage.clear();
                      navigate(href);

                    case 4:
                    case "end":
                      return _context.stop();
                  }
                }
              }, _callee);
            }))();
          } catch (e) {
            navigate(href);
          }
        })["catch"](function () {// ToDo: error handling
        });
      } else {
        navigate(href);
      }
    };
  };

  return /*#__PURE__*/_jsx(Drawer, {
    sx: {
      width: drawerWidth,
      flexShrink: 0,
      '& .MuiDrawer-paper': {
        width: drawerWidth,
        boxSizing: 'border-box'
      }
    },
    variant: "permanent",
    anchor: "left",
    children: /*#__PURE__*/_jsxs(StyledContentContainer, {
      children: [/*#__PURE__*/_jsxs(Box, {
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsxs(Box, {
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          children: [/*#__PURE__*/_jsx(StyledIconBox, {
            mr: 1.5,
            children: /*#__PURE__*/_jsx(Icon, {
              src: logo,
              width: 29,
              height: 29
            })
          }), /*#__PURE__*/_jsx(Tooltip, {
            title: ((_ref2 = "".concat((authUser === null || authUser === void 0 ? void 0 : authUser.first_name) || '', " ").concat((authUser === null || authUser === void 0 ? void 0 : authUser.last_name) || '')) === null || _ref2 === void 0 ? void 0 : _ref2.length) > 24 ? "".concat((authUser === null || authUser === void 0 ? void 0 : authUser.first_name) || '', " ").concat((authUser === null || authUser === void 0 ? void 0 : authUser.last_name) || '') : '',
            placement: "bottom-start",
            children: /*#__PURE__*/_jsx(Typography, {
              color: "mineShaft",
              variant: "body1",
              fontWeight: "700",
              children: longTextReplacer("".concat((authUser === null || authUser === void 0 ? void 0 : authUser.first_name) || '', " ").concat((authUser === null || authUser === void 0 ? void 0 : authUser.last_name) || ''), 24)
            })
          })]
        }), /*#__PURE__*/_jsx(IconButton, {
          onClick: handleOpenUserMenu,
          children: /*#__PURE__*/_jsx(Icon, {
            src: settings,
            width: 32,
            height: 32
          })
        }), /*#__PURE__*/_jsx(Menu, {
          id: "menu-appbar",
          anchorEl: anchorElUser,
          anchorOrigin: {
            vertical: 'center',
            horizontal: 'center'
          },
          keepMounted: true,
          transformOrigin: {
            vertical: 'center',
            horizontal: 'center'
          },
          PaperProps: {
            style: {
              borderRadius: pxToRem(14),
              boxShadow: '0px 4px 16px rgba(110, 118, 143, 0.3)',
              minWidth: pxToRem(194)
            }
          },
          open: Boolean(anchorElUser),
          onClose: handleCloseUserMenu,
          children: settingsItems.map(function (setting) {
            return setting.mailLink ? /*#__PURE__*/_jsxs(StyledMenuItem, {
              component: "a",
              href: setting.href,
              children: [setting.icon, /*#__PURE__*/_jsx(StyledTypography, {
                ml: 0.75,
                textAlign: "center",
                color: "waterloo",
                children: setting.title
              })]
            }, setting.id) : /*#__PURE__*/_jsxs(StyledMenuItem, {
              disabled: location.pathname === Routes.Settings && setting.href === Routes.Settings,
              onClick: handleChooseMenu(setting.href),
              children: [setting.icon, /*#__PURE__*/_jsx(StyledTypography, {
                ml: 0.75,
                textAlign: "center",
                color: "waterloo",
                children: setting.title
              })]
            }, setting.id);
          })
        })]
      }), /*#__PURE__*/_jsx(Box, {
        pb: 5,
        pt: 5,
        children: /*#__PURE__*/_jsx(Typography, {
          variant: "h3",
          fontWeight: "bold",
          children: t('components.sideBar.adminPanel')
        })
      }), /*#__PURE__*/_jsx(Box, {
        sx: {
          flexGrow: 1
        },
        children: sideBarItems.map(function (item) {
          return /*#__PURE__*/_jsx(NavItem, {
            href: item.href,
            icon: item.icon,
            title: item.title
          }, item.id);
        })
      })]
    })
  });
};

export { SideBarComponent };